import { useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, SectionHead } from "@/components/Reveal";
import { AreaCta, AreaLinkCard, AreaSearch, GettingHere } from "@/components/Areas";
import { FindUs } from "@/components/home/HomeTop";
import { AREAS_BY_STATE, DC_WARD_NUMBERS, STATES } from "@/lib/areas";
import { BUSINESS } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const LEADS = {
  "washington-dc": "Licensed by ABCA and located at 1738 14th St NW, District Gaslight serves medical cannabis patients across all 8 wards of the District. Order ahead from our live menu and pick up upstairs, next to Chicken + Whiskey.",
  maryland: "Maryland patients can shop with us as visitors: register with ABCA's non-DC form (or bring a valid medical card from Maryland), order ahead and pick up at our 14th Street NW shop.",
  virginia: "Virginia patients can shop with us as visitors: register with ABCA's non-DC form (or bring a valid medical card from Virginia), order ahead and pick up at our 14th Street NW shop.",
};

const WardBlock = ({ ward, areas, i }) => (
  <Reveal delay={0.03 * i} className="rounded-2xl border border-neutral-200 bg-white p-6" >
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-lime font-display text-lg font-black">{ward}</span>
      <div>
        <h3 className="font-display text-xl font-black tracking-tight">Ward {ward}</h3>
        <p className="text-xs font-semibold text-neutral-500">{areas.length} neighborhoods</p>
      </div>
    </div>
    <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-testid={`ward-${ward}-list`}>
      {areas.map((a) => <li key={a.slug}><AreaLinkCard a={a} /></li>)}
    </ul>
  </Reveal>
);

export default function StateAreas() {
  const { state } = useParams();
  const s = STATES[state];
  if (!s) return <NotFound />;
  const areas = AREAS_BY_STATE[state];
  const isDC = state === "washington-dc";
  const crumbs = [{ name: "Home", path: "/" }, { name: "Areas We Serve", path: "/areas-we-serve" }, { name: s.name, path: `/areas-we-serve/${state}` }];
  const sorted = [...areas].sort((a, b) => a.mi - b.mi);

  return (
    <>
      <Seo
        title={isDC ? "Medical Cannabis Dispensary in Washington, DC — All 8 Wards | District Gaslight" : `Medical Dispensary Near ${s.name} — DC Pickup for ${s.short} Patients | District Gaslight`}
        description={isDC
          ? `District Gaslight on 14th St NW serves ${areas.length} DC neighborhoods across all 8 wards. Find directions, Metro and pickup info for your neighborhood.`
          : `${s.name} patients: order ahead and pick up at District Gaslight, 1738 14th St NW, DC. Directions and Metro tips from ${areas.length} ${s.short} towns.`}
        path={`/areas-we-serve/${state}`}
        crumbs={crumbs}
      />
      <PageHeader crumbs={crumbs} eyebrow={`${s.label} · ${areas.length} areas`} title={isDC ? "Medical cannabis dispensary across" : "Medical cannabis pickup for"} highlight={isDC ? "all 8 DC wards" : `${s.name} patients`} image={isDC ? "/img/store-1.webp" : "/img/storefront.webp"} lead={LEADS[state]} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20" data-testid={`state-areas-${state}`}>
        <Reveal className="mb-8"><SectionHead eyebrow={`Search ${s.short}`} title={isDC ? "Find your DC neighborhood" : `Find your ${s.short} town`} /></Reveal>
        <AreaSearch areas={areas} label={`Search ${s.name} areas`} />
        {isDC ? (
          <div className="mt-12 grid gap-4 lg:grid-cols-2">
            {DC_WARD_NUMBERS.map((w, i) => <WardBlock key={w} ward={w} i={i} areas={areas.filter((a) => a.ward === w)} />)}
          </div>
        ) : (
          <ul className="mt-12 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid={`${state}-list`}>
            {sorted.map((a) => <li key={a.slug}><AreaLinkCard a={a} /></li>)}
          </ul>
        )}
        {!isDC && (
          <p className="mt-8 max-w-3xl rounded-2xl bg-brand-sand p-5 text-sm text-neutral-600">
            Before you visit, <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-red">register with ABCA's non-DC form</a> or bring a valid medical cannabis card from home. Cannabis purchased in DC should not be transported across state lines.
          </p>
        )}
      </section>
      <FindUs />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <Reveal className="mb-10"><SectionHead eyebrow="Getting here" title="Metro, walking & rideshare" /></Reveal>
        <GettingHere />
      </section>
      <AreaCta where={`areas-${state}`} />
    </>
  );
}
